import { Entity } from '@ecosystem/newron-core/types';
import {
  BrandCompetitorOptions,
  BrandCompetitorsType,
  CompetitorCheckBoxOptions,
  CompetitorRadioOptions,
  CompetitorTypeOption,
} from './constant';
import { cloneDeep } from 'lodash';
import { ListItem } from '@ecosystem/newron-design-system/build/components/listBox/types';
import { inputs } from '@ecosystem/newron-design-system';
import { CompetitorCheckBoxState } from './types';
const { CheckboxState } = inputs;
export const getFlattenedCompetitorOptions = (options): Entity[] => {
  return options.flatMap(item => {
    if (item.options) {
      const optionsWithParent = item.options.map(opt => ({
        ...opt,
        parent: item.value,
      }));

      const parentWithUpdatedOptions = {
        ...item,
        options: optionsWithParent,
      };

      return [parentWithUpdatedOptions, ...optionsWithParent];
    }
    return [item];
  });
};

export const getFlatOptionsWithOwned = brandOptions => {
  const flatOptions = brandOptions
    .flatMap(group => [group.label, ...(group.options || [])])
    .filter((opt: unknown): opt is Entity => typeof opt !== 'string');

  const flatOptionsWithOwned: Entity[] = flatOptions.flatMap((item: any) => {
    if (Array.isArray(item?.options)) {
      return [item, ...(item.options || [])];
    }
    return item;
  });

  return flatOptionsWithOwned;
};

export const getDisabledCompetitorBrandsOptions = (
  brandOptionsToAnalyse: Entity[],
  highlightedBrandOption: string,
  isRemoved: boolean
): Entity[] => {
  return (
    brandOptionsToAnalyse?.map(group => {
      const disabledOptions = getDisabledOptions(group, highlightedBrandOption, isRemoved);
      return {
        ...group,
        options: disabledOptions,
      };
    }) || []
  );
};

export const disableSelectedCompetitorBrandOptions = (allBrandOptions: any[], selectedBrandOptions: Entity[]) => {
  allBrandOptions.forEach(brandOption => {
    brandOption.isDisabled = selectedBrandOptions.some(selected => selected.value === brandOption.value);

    //check if the brand owner is selected
    if (brandOption.type === BrandCompetitorsType.OWNER) {
      brandOption.options.forEach(option => {
        //if the brand owner is selected, disable all its owned brands
        //or check each of the owned brands if they are selected, if so disable them
        const isOwnedBrandOptionDisabled = (brandOption.isDisabled ?? false) as boolean;
        const shouldDisableOwnedBrand =
          isOwnedBrandOptionDisabled || selectedBrandOptions.some(selected => selected.value === option.value);
        option.isDisabled = shouldDisableOwnedBrand;
      });

      //if some owned brand that belongs to the brand owner is disabled
      //then we disable the brand owner as well
      //this is to not allow the user to compare the brand owner with its owned brands
      if (brandOption.options.some(opt => opt.isDisabled)) {
        brandOption.isDisabled = true;
      }
    }
  });
  disableSharedBrandsForSelectedBrandOwnerGroups(allBrandOptions, selectedBrandOptions);
};

export const disableSharedBrandsForSelectedBrandOwnerGroups = (
  allBrandOptions: any[],
  selectedBrandOptions: Entity[]
) => {
  const selectedBrandOwnerOptions = allBrandOptions.filter(b =>
    selectedBrandOptions.some(selected => selected.value === b.value && b.type === BrandCompetitorsType.OWNER)
  );
  const allOwnedBrands = selectedBrandOwnerOptions.flatMap(b => b.options ?? []);
  allBrandOptions.forEach(brandOption => {
    const shouldDisable = allOwnedBrands.some(selected => selected.value === brandOption.value);
    brandOption.isDisabled = shouldDisable ? true : brandOption.isDisabled;

    //check for the brands within the brand owner group
    if (brandOption.type === BrandCompetitorsType.OWNER) {
      brandOption.options.forEach(option => {
        //if the shared owned brand is selected, disable them
        const shouldDisableOwnedBrand = allOwnedBrands.some(selected => selected.value === option.value);
        option.isDisabled = shouldDisableOwnedBrand ? true : option.isDisabled;
      });
    }
  });
};

const getDisabledOptions = (group: Entity, highlightedBrandOption: string, isRemoved: boolean) => {
  return group?.options?.map(opt => {
    const isBrandOwnerHighlighted = highlightedBrandOption === opt.value;
    const isOwnedBrandHighlighted = opt.options?.some(o => o.value === highlightedBrandOption);
    if (!isBrandOwnerHighlighted && !isOwnedBrandHighlighted) {
      return opt;
    }
    if (isBrandOwnerHighlighted) {
      return processWhenBoIsHighlighted(opt, isRemoved);
    }
    if (isOwnedBrandHighlighted) {
      return processWhenOwnedBrandIsHighlighted(opt, highlightedBrandOption, isRemoved);
    }
  });
};

export const getUpdatedCompetitorBrandOptions = (
  originalCompetitorBrandOptions: Entity[] = [],
  selectedCompetitorBrandOptions: Entity[] = [],
  highlightedBrand: Entity[] = []
) => {
  //here we perform a deepClone so that nested objects are also cloned and not referenced
  //this is because we are mutating the isDisabled property of the options
  const updatedCompetitorBrandsOptions = cloneDeep(originalCompetitorBrandOptions);
  updatedCompetitorBrandsOptions.forEach(brandOptions => {
    disableSelectedCompetitorBrandOptions(brandOptions.options, [
      ...selectedCompetitorBrandOptions,
      ...highlightedBrand,
    ]);
  });
  return updatedCompetitorBrandsOptions;
};

/**
 * Get competitor options filtered by brands or brand owners
 * @param competitorType competitor type - brands or brand owners
 * @param competitorOptions list of competitor options to filter
 * @returns competitor options filtered by brands or brand owners
 */
export const getCompetitorBrandsOptionsBasedOnType = (
  competitorType: ListItem,
  competitorOptions: Entity[]
): Entity[] => {
  const competitorBrandsOptionsFnMap = {
    [CompetitorTypeOption.Brands.value]: getCompetitorBrandsOptions,
    [CompetitorTypeOption.BrandOwners.value]: getCompetitorBrandOwnerOptions,
  };
  return competitorBrandsOptionsFnMap[competitorType.value as string](competitorOptions);
};

const getCompetitorBrandOwnerOptions = (competitorOptions: Entity[]): Entity[] => {
  const brandOwnersPredicate = (brand: Entity) =>
    brand.type === BrandCompetitorsType.INDEPENDENT || brand.type === BrandCompetitorsType.OWNER;
  const competitorBrandsOptionsBasedOnType = getFlatOptionsWithOwned(structuredClone(competitorOptions)).filter(
    brandOwnersPredicate
  );
  /**
   * We need to make sure that the sub-options (if there are any) are not included in the final list when filtering by Brand Owners
   */
  competitorBrandsOptionsBasedOnType.forEach(brandOwner => delete brandOwner.options);
  return competitorBrandsOptionsBasedOnType;
};

const getCompetitorBrandsOptions = (competitorOptions: Entity[]): Entity[] => {
  /**
   * we need to remain with the original structure of the competitor options
   * but we need to disable the brand owner options so that they are not selectable
   */
  const competitorBrandsOptions = structuredClone(competitorOptions);
  getFlatOptionsWithOwned(competitorBrandsOptions).forEach((option: any) => {
    if (option.type === BrandCompetitorsType.OWNER) {
      option.isDisabled = true;
    }
  });
  return competitorBrandsOptions;
};

const processWhenBoIsHighlighted = (opt: any, isRemoved: boolean): Entity => {
  return {
    ...opt,
    isDisabled: !isRemoved,
    options: opt.options?.map(o => {
      return {
        ...o,
        isDisabled: !isRemoved,
      };
    }),
  };
};

const processWhenOwnedBrandIsHighlighted = (opt: any, highlightedBrandOption: string, isRemoved: boolean): Entity => {
  const isBrandOwnerDisabled = getBrandOwnerDisabledFlag(opt, highlightedBrandOption, isRemoved);
  return {
    ...opt,
    isDisabled: isRemoved ? isBrandOwnerDisabled : true,
    options: opt.options?.map(o => {
      if (o.value === highlightedBrandOption) {
        return {
          ...o,
          isDisabled: !isRemoved,
        };
      }
      return o;
    }),
  };
};

export const getBrandOwnerDisabledFlag = (opt: Entity, highlightedBrandOption: string, isRemoved: boolean): boolean => {
  if (!isRemoved) return true;

  const disabledOptions = opt.options?.filter(o => o.isDisabled) || [];
  return !(disabledOptions.length === 1 && disabledOptions[0].value === highlightedBrandOption);
};

export const getFilteredCompetitorOptions = (competitorOptions: Entity[], brand: Entity): Entity[] => {
  return competitorOptions?.filter(opt => {
    if (brand.type === BrandCompetitorsType.OWNER) {
      return opt.value !== BrandCompetitorOptions.TopBrands;
    }
  });
};

export const getSelectedBrandOwnerGroups = (
  allSelectedOptions: Entity[],
  allBrandOptions: Entity[],
  competitorOption: string
): Entity[] => {
  return competitorOption === CompetitorRadioOptions.ExactList
    ? allSelectedOptions.filter(b =>
        allBrandOptions.some(optionValues =>
          optionValues.options?.some(option => option.value === b.value && option.type === BrandCompetitorsType.OWNER)
        )
      )
    : [];
};

export const getFormattedBrandCompetitorCategory = (
  competitorCheckBoxState: CompetitorCheckBoxState,
  competitorTypeOption: ListItem
): Entity[] => {
  const { topNCheckboxState, exactListCheckboxState } = competitorCheckBoxState;
  const topNCategory =
    topNCheckboxState === CheckboxState.CHECKED
      ? [{ value: `top${competitorTypeOption.value}`, label: `top${competitorTypeOption.value}` }]
      : [];
  const exactListCategory =
    exactListCheckboxState === CheckboxState.CHECKED
      ? [{ value: CompetitorCheckBoxOptions.ExactList, label: CompetitorCheckBoxOptions.ExactList }]
      : [];
  return [...topNCategory, ...exactListCategory];
};

export const getFormattedCompetitorBrandOptions = (
  competitorCheckBoxState: CompetitorCheckBoxState,
  topValue: Entity,
  quantityTypeValue: Entity
): Entity[] => {
  const { topNCheckboxState, exactListCheckboxState } = competitorCheckBoxState;
  const topNOption =
    topNCheckboxState === CheckboxState.CHECKED
      ? [
          {
            value: `${topValue.value}${quantityTypeValue.value}`,
            label: `${topValue.value}${quantityTypeValue.value}`,
          },
        ]
      : [];
  const exactListOption =
    exactListCheckboxState === CheckboxState.CHECKED
      ? [
          {
            value: CompetitorCheckBoxOptions.ExactList,
            label: CompetitorCheckBoxOptions.ExactList,
          },
        ]
      : [];
  return [...topNOption, ...exactListOption];
};

export const getFormattedTopNSelection = (
  topNCheckboxState: string,
  competitorTypeOption: ListItem,
  topValue: Entity,
  quantityTypeValue: Entity
): Entity[] => {
  return topNCheckboxState === CheckboxState.CHECKED
    ? [
        {
          value: `${competitorTypeOption.value}.${topValue.value}.${quantityTypeValue.value}`,
          label: `Top ${topValue.label} ${competitorTypeOption.label} by ${quantityTypeValue.label}`,
        },
      ]
    : [];
};

export const findSharedBrandsFromEntity = (brandOptions: Entity[]): Entity[] => {
  const flatOptions = getFlatOptionsWithOwned(brandOptions);
  if (!flatOptions || flatOptions.length === 0) {
    return [];
  }
  const sharedBrands = flatOptions.filter(option => option.label.endsWith(' *'));

  return sharedBrands;
};

export const getFormattedSharedBrands = (
  highlightedValue: Entity[],
  brandCompetitorType: string,
  sharedBrands: Entity[]
): Entity[] => {
  const uniqueSharedBrands: string[] = Array.from(new Set(sharedBrands.map(brand => brand.value)));
  return highlightedValue[0].type === BrandCompetitorsType.OWNER && brandCompetitorType === 'brand_name'
    ? uniqueSharedBrands.map(brand => ({ value: brand, label: brand }))
    : [];
};

export const getFormattedExcludedBrandOwners = (
  highlightedValue: Entity[],
  brandCompetitorType: string,
  sharedBrands: Entity[]
): Entity[] => {
  const [highlighted] = highlightedValue;
  if (brandCompetitorType !== 'brand_owner_name' || !highlighted) return [];

  if (highlighted.label.endsWith(' *')) {
    const uniqueOwners = Array.from(
      new Set(
        sharedBrands
          .filter(brand => brand.value === highlighted.value)
          .map(brand => brand.brandOwner)
          .filter(Boolean)
      )
    );
    return uniqueOwners.map(value => ({ value, label: value }));
  }

  if (highlighted.type === BrandCompetitorsType.OWNED) {
    return [{ value: highlighted.brandOwner, label: highlighted.brandOwner }];
  }

  return [];
};
